/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { Alert, Button, Checkbox, FormControl, FormControlLabel, Snackbar, TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { NumericFormat } from 'react-number-format';
import { usePullPayoffContext } from '../../contexts/PullPayoffContext';
import LoanInfoStyles from '../../css/LoanInformation.module.css';
import Styles from '../../css/PullPayoffPage.module.css';
import EnvelopeWithBox from '../EnvelopeWithBox';
import LoadingModal from '../modals/LoadingModal';
import PullPayoffModal from '../modals/PullPayoffModal';
import { useAuthContext } from '../../contexts/AuthContext';
import { useViewingContext } from '../../contexts/ViewingContext';

const PullPayoffForm = () => {
  const pullPayoffContext = usePullPayoffContext();
  const { fullAccess } = useAuthContext();
  const { isServicerView } = useViewingContext();

  return (
    <>
      <section className={Styles.errorList}>
        <ul>
          {pullPayoffContext.disablePullPayoff && !pullPayoffContext.loadingDataError && (
            <li className={Styles.errorMsg}>
              The loan is not current or requires additional input. Kindly contact the customer service team to generate
              the payoff for you.
            </li>
          )}
          {pullPayoffContext.disablePullPayoff && pullPayoffContext.loadingDataError && (
            <li className={Styles.errorMsg}>There was an error loading the loan data.</li>
          )}
          {pullPayoffContext.payoffLetterFeeAgreedError && (
            <li className={Styles.errorMsg}>
              Borrower has not agreed to the Payoff Letter Fee, so Payoff cannot be pulled.
            </li>
          )}
        </ul>
      </section>
      <EnvelopeWithBox blockName={'Pull Payoff Letter'}>
        <form onSubmit={pullPayoffContext.validateFields}>
          {pullPayoffContext.status === 'loading' && (
            <LoadingModal
              open={pullPayoffContext.status === 'loading'}
              loadingMessage={pullPayoffContext.loadingMessage}
            />
          )}
          {pullPayoffContext.pullPayoffModalOpen && <PullPayoffModal />}
          <Snackbar
            open={!!pullPayoffContext.statusAlert.message}
            onClose={() =>
              pullPayoffContext.setStatusAlert({
                status: '',
                message: '',
              })
            }
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            ClickAwayListenerProps={{ onClickAway: () => null }}
          >
            <Alert
              onClose={() =>
                pullPayoffContext.setStatusAlert({
                  status: '',
                  message: '',
                })
              }
              severity={pullPayoffContext.statusAlert.status as any}
              variant='filled'
              className={Styles.alert}
            >
              {pullPayoffContext.statusAlert.message}
            </Alert>
          </Snackbar>
          <section className={Styles.desktopTable}>
            <section className={LoanInfoStyles.loanInfoTable}>
              <section className={LoanInfoStyles.leftColumn}>
                <table className={LoanInfoStyles.loanInfoTableSection}>
                  <tbody>
                    <tr>
                      <th>Principal</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.upb}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>YTD MIP</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.mipYTD}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Escrow 2</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.escrow2}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Reg. Suspense</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.regSuspense}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Sub. Suspense</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.subSuspense}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Unpaid Late Charges</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.unpaidLateCharges}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Corp Advances</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.corpAdvances}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Deferred Principal</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.deferredPrincipal}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>
              <section className={LoanInfoStyles.rightColumn}>
                <table className={LoanInfoStyles.loanInfoTableSection}>
                  <tbody className={Styles.desktopTable}>
                    <tr>
                      <th>YTD Interest</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.interestPaidYTD}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Escrow</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.escrow}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Escrow 3</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.escrow3}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Oth. Suspense</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.otherSuspense}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Loss Drafts</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.lossDrafts}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Assessments</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.assessments}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Deferred Interest</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.deferredInterest}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>
            </section>
          </section>
          <section className={Styles.mobileTable}>
            <section className={LoanInfoStyles.loanInfoTable}>
              <section className={LoanInfoStyles.leftColumn}>
                <table className={LoanInfoStyles.loanInfoTableSection}>
                  <tbody className={Styles.mobileTable}>
                    <tr>
                      <th>Principal</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.upb}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>YTD Interest</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.interestPaidYTD}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>YTD MIP</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.mipYTD}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Escrow</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.escrow}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Escrow 2</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.escrow2}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Escrow 3</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.escrow3}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Reg. Suspense</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.regSuspense}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Oth. Suspense</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.otherSuspense}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Sub. Suspense</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.subSuspense}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Loss Drafts</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.lossDrafts}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Unpaid Late Charges</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.unpaidLateCharges}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Assessments</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.assessments}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Corp Advances</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.corpAdvances}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Deferred Interest</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.deferredInterest}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>Deferred Principal</th>
                      <td>
                        <NumericFormat
                          value={pullPayoffContext.loanBalancesInfo.deferredPrincipal}
                          prefix={'$'}
                          thousandSeparator=','
                          decimalScale={2}
                          fixedDecimalScale
                          displayType='text'
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>
            </section>
          </section>
          <section className={Styles.flexCol}>
            <section className={Styles.gridContainer}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <FormControl>
                  <DatePicker
                    label='Interest Through Date *'
                    slotProps={{
                      textField: {
                        helperText: pullPayoffContext.interestThroughDateError
                          ? pullPayoffContext.interestThroughDateErrorMsg
                          : ' ',
                        error: pullPayoffContext.interestThroughDateError,
                        size: 'small',
                      },
                    }}
                    value={pullPayoffContext.interestThroughDate}
                    onChange={(value) => {
                      pullPayoffContext.setInterestThroughDate(value);
                    }}
                    disabled={pullPayoffContext.disablePullPayoff}
                  />
                </FormControl>
              </LocalizationProvider>
              {isServicerView && (
                <NumericFormat
                  size='small'
                  label='Payoff Wire/Check Amount'
                  placeholder='Payoff Wire/Check Amount'
                  value={pullPayoffContext.paymentAmount}
                  disabled={pullPayoffContext.disablePullPayoff}
                  customInput={TextField}
                  thousandSeparator={true}
                  prefix={'$'}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  helperText=' '
                  onChange={(e) => {
                    pullPayoffContext.setPaymentAmount(Number(e.target.value.replace(/[$,\s]/g, '')));
                    pullPayoffContext.setPaymentAmountError(Number(e.target.value.replace(/[$,\s]/g, '')) < 0);
                  }}
                />
              )}
              {isServicerView && (
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={pullPayoffContext.disablePullPayoff}
                      checked={pullPayoffContext.freezeDisbursements}
                      onChange={() => pullPayoffContext.setFreezeDisbursements(!pullPayoffContext.freezeDisbursements)}
                    />
                  }
                  className={Styles.text}
                  label='Freeze Escrow Disbursements'
                />
              )}
              {/*
                conventional loans should always include Deferred principal.
                hide checkbox if conventional
              */}
              {isServicerView && !pullPayoffContext.loanConventional && (
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={pullPayoffContext.disablePullPayoff}
                      checked={!pullPayoffContext.addDeferredPrincipal}
                      onChange={() =>
                        pullPayoffContext.setAddDeferredPrincipal(!pullPayoffContext.addDeferredPrincipal)
                      }
                    />
                  }
                  className={Styles.text}
                  label='Do not add Deferred Principal'
                />
              )}
              {isServicerView && !pullPayoffContext.hideReconveyanceCheckbox && (
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={pullPayoffContext.disablePullPayoff}
                      checked={!pullPayoffContext.addReconveyanceFee}
                      onChange={() => pullPayoffContext.setAddReconveyanceFee(!pullPayoffContext.addReconveyanceFee)}
                    />
                  }
                  className={Styles.text}
                  label='Do not add Reconveyance Fee'
                />
              )}
              {isServicerView && (
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={pullPayoffContext.disablePullPayoff}
                      checked={pullPayoffContext.externalPayoff}
                      onChange={() => {
                        pullPayoffContext.setExternalPayoff(!pullPayoffContext.externalPayoff);
                      }}
                    />
                  }
                  className={Styles.text}
                  label='External Payoff'
                />
              )}
              {isServicerView && (
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={pullPayoffContext.disablePullPayoff}
                      checked={pullPayoffContext.uploadPayoff}
                      onChange={() => pullPayoffContext.setUploadPayoff(!pullPayoffContext.uploadPayoff)}
                    />
                  }
                  className={Styles.text}
                  label='Post Comment and Store Document'
                />
              )}
            </section>
          </section>
          <section className={Styles.payoffError}>
            {pullPayoffContext.errors?.generic && (
              <p className={Styles.errorMsg}>Error generating payoff: {pullPayoffContext.errors?.generic}</p>
            )}
          </section>
          <section className={Styles.submit}>
            <Button type='submit' size='large' className={Styles.button} disabled={pullPayoffContext.disablePullPayoff || (!fullAccess && isServicerView)}>
              Pull Payoff
            </Button>
          </section>
        </form>
        <section />
      </EnvelopeWithBox>
    </>
  );
};

export default PullPayoffForm;
